
import React from "react";
import { Button } from "@/components/ui/button";
import { MessageSquarePlus, Trash2, History, Copy, Menu } from "lucide-react";
import { Message } from "./message-item";
import { useToast } from "@/hooks/use-toast";
import { useSidebar } from "@/components/ui/sidebar";

interface ChatHeaderProps {
  title: string;
  onDelete: () => void;
  messages: Message[];
  onNewChat: () => void;
  isMobile?: boolean;
  onShowHistory?: () => void;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  title,
  onDelete,
  messages,
  onNewChat,
  isMobile = false,
  onShowHistory
}) => {
  const { toast } = useToast();
  const { toggleSidebar } = useSidebar();

  const handleCopyConversation = () => {
    // Create a formatted text version of the conversation
    const conversationText = messages
      .filter(msg => msg.role !== 'system')
      .map(msg => {
        const role = msg.role === 'user' ? 'Bruker' : 'Klara';
        return `${role}: ${msg.content}`;
      })
      .join('\n\n');

    navigator.clipboard.writeText(conversationText).then(() => {
      toast({
        title: "Samtale kopiert",
        description: "Samtalen er kopiert til utklippstavlen.",
      });
    }).catch(() => {
      toast({
        title: "Kopiering feilet",
        description: "Kunne ikke kopiere samtalen til utklippstavlen.",
        variant: "destructive",
      });
    });
  };

  // Simplified logic to determine if we have conversation content
  const hasContent = messages.length > 0 && messages.some(msg => msg.role === 'user');

  return (
    <div className="flex items-center justify-between p-4 border-b">
      <div className="flex items-center gap-2">
        {isMobile && (
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            className="flex items-center gap-2"
          >
            <Menu className="h-4 w-4" />
            Meny
          </Button>
        )}
        {isMobile && onShowHistory && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onShowHistory}
            className="flex items-center gap-2"
          >
            <History className="h-4 w-4" />
            Historikk
          </Button>
        )}
        <h1 className={`font-semibold truncate ${isMobile ? 'text-sm max-w-32' : 'text-lg max-w-64'}`}>
          {title}
        </h1>
      </div>
      
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onNewChat}
          className="flex items-center gap-2"
        >
          <MessageSquarePlus className="h-4 w-4" />
          {!isMobile && "Ny samtale"}
        </Button>
        
        {hasContent && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyConversation}
              className="flex items-center gap-2"
            >
              <Copy className="h-4 w-4" />
              {!isMobile && "Kopier"}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={onDelete}
              className="flex items-center gap-2 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              {!isMobile && "Slett"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
